import {
    FormConfiguration,
    FormConfigurationComplexOptionDefinition,
    FormConfigurationDataset,
    FormConfigurationElement,
    FormConfigurationElementControlBase,
    FormConfigurationElementParagraph,
    FormConfigurationElementType,
    FormConfigurationOptionsDefinitionDynamic,
    FormConfigurationOptionsType
} from "@fhir-api";

export interface CatalogDefinition {
    project: string;
    datasetName: string;
    optionDefinition: FormConfigurationOptionsDefinitionDynamic;
}

export function extractCatalogDefinitions(
    formConfiguration: FormConfiguration
): CatalogDefinition[] {
    const catalogs: CatalogDefinition[] = [];

    for (const dataset of formConfiguration.datasets) {
        collectCatalogFromDataset(dataset, catalogs);
    }

    return catalogs;
}

export function buildOptionNameToTargetRefMap(
    optionDefinitions: FormConfigurationComplexOptionDefinition[]
): Record<string, string> {
    return optionDefinitions.reduce(
        (acc, option) => ((acc[option.name] = option.targetFieldRefName), acc),
        {} as Record<string, string>
    );
}

export function mapRawCatalogRow(
    row: Record<string, string | number>,
    nameToTargetRef: Record<string, string>
): Record<string, string | number> {
    const targetRefValues = new Set(Object.values(nameToTargetRef));
    const mapped: Record<string, string | number> = {};

    for (const [key, value] of Object.entries(row)) {
        if (!targetRefValues.has(key)) {
            mapped[key] = value;
        }
    }

    for (const [name, targetRef] of Object.entries(nameToTargetRef)) {
        if (targetRef in row) {
            mapped[name] = row[targetRef];
        }
    }

    return mapped;
}

export function filterCachedCatalogData(
    cachedData: Record<string, string | number>[],
    searchText: string,
    searchField: string | undefined,
    optionDefinitions: FormConfigurationComplexOptionDefinition[],
    mapOptionToLabel: (datasetUri: string, name: string, value: string) => string | undefined
): Record<string, string | number>[] {
    const lowerSearch = searchText.toLowerCase();

    const filtered = searchField
        ? cachedData.filter(row => {
              const value = row[searchField];
              return value != null && value.toString().toLowerCase().includes(lowerSearch);
          })
        : cachedData.filter(row =>
              Object.values(row).some(
                  value => value != null && value.toString().toLowerCase().includes(lowerSearch)
              )
          );

    return filtered.map(row => {
        const mapped = { ...row };

        Object.entries(mapped).forEach(([key, value]) => {
            const sourceAttribute =
                optionDefinitions.find(option => option.name === key)?.sourceAttribute ?? "";
            const datasetUri = sourceAttribute.substring(0, sourceAttribute.lastIndexOf("."));
            const mappedValue = mapOptionToLabel(datasetUri, key, value.toString());
            mapped[key] = mappedValue ?? value.toString();
        });

        return mapped;
    });
}

function collectCatalogFromDataset(
    dataset: FormConfigurationDataset,
    result: CatalogDefinition[]
): void {
    const allElements: FormConfigurationElement[] = [
        ...dataset.sections.flatMap(s => s.categories).flatMap(c => c.elements),
        ...(dataset.initializationSection?.categories.flatMap(c => c.elements) ?? [])
    ];

    for (const element of allElements) {
        const prefillDef = getPrefillOptionsDef(element);
        if (
            prefillDef &&
            prefillDef.type === FormConfigurationOptionsType.DynamicOptionSet &&
            prefillDef.optionsSource?.sourceDataset?.catalog &&
            prefillDef.optionsSource?.sourceDataset?.project
        ) {
            const { project, name: datasetName } = prefillDef.optionsSource.sourceDataset;
            const alreadyAdded = result.some(
                c => c.project === project && c.datasetName === datasetName
            );
            if (!alreadyAdded) {
                result.push({
                    project: project!,
                    datasetName,
                    optionDefinition: prefillDef
                });
            }
        }
    }
}

function getPrefillOptionsDef(
    element: FormConfigurationElement
): FormConfigurationOptionsDefinitionDynamic | null {
    if (element.type === FormConfigurationElementType.Control) {
        const control = element as FormConfigurationElementControlBase;
        if (
            control.prefillOptionsDef?.type === FormConfigurationOptionsType.DynamicOptionSet
        ) {
            return control.prefillOptionsDef as FormConfigurationOptionsDefinitionDynamic;
        }
    }
    if (element.type === FormConfigurationElementType.Paragraph) {
        const paragraph = element as FormConfigurationElementParagraph;
        if (
            paragraph.prefillOptionsDef?.type === FormConfigurationOptionsType.DynamicOptionSet
        ) {
            return paragraph.prefillOptionsDef;
        }
    }
    return null;
}
